
import {computed, defineComponent, onBeforeUnmount, onMounted, reactive, Ref, ref, toRefs} from "vue";
import ModalDialog from "@/components/ModalDialog.vue";
import EmployeeWageForm from "@/views/Employee/forms/EmployeeWageForm.vue";
import CurrencyField from "@/components/CurrencyField.vue";
import Wage from "@/model/Wage";
import moment from "moment/moment";
import store from "@/store/store";
import {helpers, required, requiredIf} from "@vuelidate/validators";
import useVuelidate from "@vuelidate/core";
import eventBus from "@/eventbus";
import {MessageTypes} from "@/components/message-types";
import EmployeeService from "@/service/EmployeeService";
import {AxiosError} from "axios";

export default defineComponent ({
    name: "EmployeeDetailsCardReactivationForm",
    components: {CurrencyField, EmployeeWageForm, ModalDialog},
    props: {
      editable: {
        type: Boolean,
        default: false
      },
      wage: {
        type: Wage,
        default: () => new Wage(),
      },
    },
    setup(props, { emit }) {
      const showDialog = ref(false);
      const uuid = ref<string | null>(null);
      const firstName = ref("");
      const lastName = ref("");
      const initialState = {
        editItem: new Wage() as Wage,
        serverErrors: new Map<string, string>(),
        contractExpiryDateMenu: false,
        contractExpiryDate: null,
      };
      const state = reactive({...initialState});

      const openDialog = (payload: {uuid: string, firstName: string, lastName:string}) => {
        uuid.value = payload.uuid;
        firstName.value = payload.firstName;
        lastName.value = payload.lastName;

        showDialog.value = !!uuid.value;
      }
      const closeDialog = () => {
        showDialog.value = false;
        uuid.value = null;
        firstName.value = "";
        lastName.value = "";
      }
      onMounted(() => {
        eventBus.$on(MessageTypes.OPEN_EMPLOYEE_REACTIVATE, openDialog);
      });
      onBeforeUnmount(() => {
        eventBus.$off(MessageTypes.OPEN_EMPLOYEE_REACTIVATE, openDialog);
      });

      const rules = computed(() => ({
        editItem: {
          validFrom: {
            required: helpers.withMessage("Gültig ab ist erforderlich.", required),
          },
          wage: {
            required: helpers.withMessage("Gehalt ist erforderlich.", required),
          },
          wageType: {
            required: helpers.withMessage("Lohnart ist erforderlich.", required),
          },
          targetHours: {
            required: helpers.withMessage("Sollstunden ist erforderlich.", requiredIf(function () {
              return isSalary.value;
            })),
          },
          contractExpiryDate: {
            required: helpers.withMessage("Vetragsende muss nach dem Startdatum sein", function () {
              return !state.contractExpiryDate ||
                  !state.editItem.validFrom ||
                  moment(state.contractExpiryDate).isAfter(state.editItem.validFrom);
            })
          }
        }
      }));

      const vuelidate = useVuelidate(rules, state);

      const errors = computed(() => {
        const errors = new Map<string, string | Ref<string>>();
        if (state.serverErrors.size > 0) {
          return state.serverErrors;
        } else {
          const model = vuelidate.value.editItem;
          if (model.$dirty) {
            if(model.validFrom.$error) {
              errors.set("validFrom", model.validFrom.$errors[0].$message);
            }
            if(model.wage.$error) {
              errors.set("wage", model.wage.$errors[0].$message);
            }
            if(model.wageType.$error) {
              errors.set("wageType", model.wageType.$errors[0].$message);
            }
            if(model.targetHours.$error) {
              errors.set("targetHours", model.targetHours.$errors[0].$message);
            }
            if(model.contractExpiryDate.$error) {
              errors.set("contractExpiryDate", model.contractExpiryDate.$errors[0].$message);
            }
          }
        }

        return errors;
      });
      const earliestValidFrom = computed(() => {
        return store.state.general.earliestValidFrom;
      });

      const isEditable = computed(() => {
        return props.editable && props.wage && moment(props.wage.validFrom).isSameOrAfter(moment(earliestValidFrom.value));
      });

      const isSalary = computed(() => {
        return state.editItem && state.editItem.wageType && state.editItem.wageType === "SAL";
      });
      const save = () => {
        state.serverErrors = new Map<string, string>();
        vuelidate.value.$reset();
        vuelidate.value.$touch();

        if (!vuelidate.value.$invalid && uuid.value) {
          const wage = state.editItem;
          const targetHours = wage.targetHours === null || wage.targetHours === undefined || `${wage.targetHours}` === ""
            ? null
            : Number(wage.targetHours);
          EmployeeService.reactivateEmployee(uuid.value, {
            wage: {
              amount: wage.wage,
              wageType: wage.wageType,
              targetHours: targetHours,
              validFrom: moment(wage.validFrom).format("YYYY-MM")
            },
            // The date picker only selects a month, while the v3 API expects a full date
            contractExpiryDate: state.contractExpiryDate
              ? moment(state.contractExpiryDate).endOf("month").format("YYYY-MM-DD")
              : null
          })
            .then(() => {
              eventBus.$emit(MessageTypes.SNACKBAR_SUCCESS, "Mitarbeiter wurde reaktiviert.");
              // v3 answers with its own employee form, so the details page reloads the employee instead
              emit("reactivated");
              showDialog.value = false;
            })
            // An already active employee gets a 409, which the global error handler already shows.
            .catch((error: AxiosError) => {
              if (error && error.response && error.response.status === 422) {
                // @ts-ignore
                eventBus.$emit(MessageTypes.ERROR, error.response.data.detail || error.response.data.title);
              }
            });
        }
      };

      return {
        showDialog,
        firstName,
        lastName,
        closeDialog,
        earliestValidFrom,
        ...toRefs(state),
        v$: vuelidate,
        errors,
        isEditable,
        close,
        save
      }
    }
  });
